import classNames from "classnames";
import Flags from "fleet/constant/flags";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import React from "react";
import Alert from "react-bootstrap/Alert";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import FormCheck from "react-bootstrap/FormCheck";
import FormControl from "react-bootstrap/FormControl";
import FormGroup from "react-bootstrap/FormGroup";
import FormLabel from "react-bootstrap/FormLabel";
import FormText from "react-bootstrap/FormText";
import Row from "react-bootstrap/Row";
import { Link, Redirect } from "react-router-dom";
import Select from "react-select";
import semverGt from "semver/functions/gt";
import semverLt from "semver/functions/lt";
import { BackButton } from "ui/button/back";
import { Features } from "ui/features";
import { NavLoader } from "ui/loader";
import { Notification } from "ui/notification";
import { getErrorMessage } from "util/response";
import { getOpt } from "util/select";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

const DEVICE_TYPE = "device";
const NETWORK_TYPE = "network";

class FlagsPage extends React.PureComponent {
  static get propTypes() {
    return {
      featureFlags: PropTypes.object,
      model: PropTypes.object.isRequired,
      networkUserFlags: PropTypes.array,
      readonly: PropTypes.bool.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
      type: PropTypes.oneOf([DEVICE_TYPE, NETWORK_TYPE]).isRequired,
      updateDeviceFlags: PropTypes.func.isRequired,
      updateNetworkFlags: PropTypes.func.isRequired,
    };
  }

  static get defaultProps() {
    return {
      networkUserFlags: [],
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      errorMessage: "",
      successMessage: "",
      submitted: false,
    };

    this._submit = this._submit.bind(this);
  }

  componentDidMount() {
    this._setFlagsFromProps(this.props);
  }

  componentDidUpdate(prevProps) {
    const modelChanged = !isEqual(prevProps.model, this.props.model);
    const typeChanged = prevProps.type !== this.props.type;

    if (modelChanged || typeChanged) {
      this._setFlagsFromProps(this.props);
    }
  }

  _getFlagsAsObject(userFlags) {
    if (!userFlags) {
      return {};
    }
    const flags = userFlags.reduce((acc, flag) => {
      acc[flag.name] = flag.value;

      return acc;
    }, {});

    Object.values(Flags).forEach((flag) => {
      const key = flag.key;
      const type = flag.type;
      if (key in flags) {
        // Everything is kept as string
        // Convert "true" to true, "false" to false.
        if (type === Boolean) {
          flags[key] = flags[key] === "true";
        }
      }
    });
    return flags;
  }

  _setFlagsFromProps(props) {
    const { model, networkUserFlags } = props;

    if (!model) {
      return;
    }
    const { user_flags: userFlags } = model;
    let flags = this._getFlagsAsObject(userFlags);

    if (this._isDevice()) {
      const overrideFlag = flags[Flags.OVERRIDE_NETWORK_FLAGS.key];
      const setFlagCount = Object.keys(flags).length;

      if (
        (overrideFlag === false && setFlagCount > 1) ||
        (overrideFlag === undefined && setFlagCount > 0)
      ) {
        /*
         * When there are any device level flag set,
         * override network flag should be true.
         */
        flags[Flags.OVERRIDE_NETWORK_FLAGS.key] = true;
      } else if (!flags[Flags.OVERRIDE_NETWORK_FLAGS.key]) {
        /*
         * When override flag is not set on device,
         * get the flag from network level flags,
         * this is to avoid inconsistent flags on device
         * when user tries override network flag.
         */
        flags = this._getFlagsAsObject(networkUserFlags);
      }
    }

    this.setState(flags);
  }

  _isNetwork() {
    return this.props.type === NETWORK_TYPE;
  }

  _isDevice() {
    return this.props.type === DEVICE_TYPE;
  }

  _getSuccessMessage() {
    const { model } = this.props;

    if (this._isNetwork()) {
      return <Link to={`/networks/${model.id}`}>View Network Devices</Link>;
    }

    return <Link to={`/devices/${model.device_id}`}>View Device</Link>;
  }

  _submit() {
    const { model, updateDeviceFlags, updateNetworkFlags } = this.props;

    let submitFn;
    if (this._isNetwork()) {
      submitFn = updateNetworkFlags;
    } else if (this._isDevice()) {
      submitFn = updateDeviceFlags;
    }

    if (submitFn) {
      this.setState(
        { submitted: true },
        this._doSubmit.bind(this, submitFn, model),
      );
    }
  }

  _doSubmit(submitFn, model) {
    const serviceArgs = {
      id: model.id,
      flags: this._getFlags(),
    };

    return submitFn(serviceArgs)
      .then(() => {
        this.setState({
          errorMessage: "",
          successMessage: "Flags updated successfully.",
        });

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      })
      .catch((e) => {
        this.setState({
          errorMessage: getErrorMessage(e),
          successMessage: "",
        });
      })
      .finally(() => {
        this.setState({
          submitted: false,
        });
      });
  }

  _getFlags() {
    const finalFlags = {};
    Object.values(Flags).forEach((flag) => {
      const key = flag.key;
      if (this.state[key] === undefined || this.state[key] === null) {
        return null;
      }
      finalFlags[key] = String(this.state[key]);
    });

    return finalFlags;
  }

  _updateArrayFlag(valueObj, key) {
    // In doSubmit, _getFlags will exclude flags with value
    // 'undefined' or 'null'.
    // Here we use null to indicate no option is selected
    const state = {
      [key]: valueObj ? valueObj.value : null,
    };
    this.setState(state);
  }

  _updateBooleanFlag(e, key) {
    let state = {};
    state[key] = e.currentTarget.checked;
    this.setState(state);
  }

  _updateTextFlag(e, key) {
    let state = {};
    state[key] = e.currentTarget.value;
    this.setState(state);
  }

  _groupFlags() {
    const flagValues = Object.values(Flags);

    const watchdogFlags = flagValues.filter((flag) => {
      return flag.flagType === "watchdog";
    });

    const windowFlags = flagValues.filter((flag) => {
      return flag.flagType === "window";
    });

    const otherFlags = flagValues.filter((flag) => {
      return flag.flagType === "other";
    });

    const deviceOnlyFlag = flagValues.find((flag) => {
      return flag.flagType === "device";
    });

    const webcamFlags = flagValues.filter((flag) => {
      return flag.flagType === "webcam";
    });

    return [watchdogFlags, windowFlags, otherFlags, deviceOnlyFlag, webcamFlags];
  }

  _getNetworkFlagHelpText() {
    if (this._isNetwork()) {
      return (
        <Row>
          <Col xs={12}>
            <FormText>
              {"Network flags are inherited by devices. However, devices " +
                'with "Override network flags with device flags" checked ' +
                "will use device flag settings instead of network flags."}
            </FormText>
          </Col>
        </Row>
      );
    }
  }

  _parseFlagSupport(deviceCurrentVersion, flagValidity) {
    if (!flagValidity) {
      return {
        isSupported: true,
        reason: "",
      };
    }

    // Flag setting on Network page
    if (this._isNetwork()) {
      let reason =
        "This flag will be set to the default value on player versions lower than " +
        flagValidity.startsIn;

      // Append the player version upper-limit to the help text
      if (flagValidity.endsIn) {
        reason += " or higher than " + flagValidity.endsIn;
      }
      return {
        isSupported: true,
        isNetwork: true,
        reason: reason,
      };
    }

    if (!deviceCurrentVersion) {
      if (process.env.NODE_ENV === "development") {
        console.warn("No player version detected and not on Network view");
      }
      let reason =
        "This flag will be set to the default value on player versions lower than " +
        flagValidity.startsIn;

      if (flagValidity.endsIn) {
        reason += " or higher than " + flagValidity.endsIn;
      }

      return {
        isSupported: true,
        reason: reason,
      };
    }

    const { startsIn, endsIn } = flagValidity;

    if (endsIn && semverGt(deviceCurrentVersion, endsIn)) {
      return {
        isSupported: false,
        reason: `Feature unavailable. Unsupported in Player Version > ${endsIn}`,
      };
    }

    if (semverLt(deviceCurrentVersion, startsIn)) {
      return {
        isSupported: false,
        reason: `Feature unavailable. Unsupported in Player Version < ${startsIn}`,
      };
    }

    return { isSupported: true, reason: "" };
  }

  _webcamSnapshotsEnabled() {
     if (!this.props.featureFlags) {
       return false;
     }
     return this.props.featureFlags['enable-webcam-snapshots'] || false;
   }

  _renderFlag(flag, disableFlag) {
    const { key, type, label, explanation, platforms, validity } = flag;
    const { readonly, model } = this.props;

    const flagSupport = this._parseFlagSupport(model.current_version, validity);
    const unsupported = !flagSupport.isSupported;
    const isNetwork = flagSupport.isNetwork;

    const isDisabled = readonly || disableFlag || unsupported;

    if (
      key === Flags.ENABLE_AUDIO.key &&
      !Features.isEnabled(Features.constants.ENABLE_AUDIO_FLAG)
    ) {
      return;
    }

    const helpBlock = (
      <FormText>
        {(unsupported || isNetwork) && (
          <Alert className="alert-muted small-alert">
            <i className="fa fa-info-circle" aria-hidden="true" />
            <span className="font-smaller">{flagSupport.reason}</span>
          </Alert>
        )}
        {`Supported Platforms: ${platforms.join(", ")}`}
        <br />
        {explanation} {flag.default && <b>Default:{flag.default}</b>}
      </FormText>
    );

    if (type === Boolean) {
      return (
        <FormGroup key={key} controlId={key}>
          <FormCheck
            checked={!!this.state[key]}
            disabled={isDisabled}
            label={label}
            onChange={(e) => this._updateBooleanFlag(e, key)}
            type="checkbox"
          />
          {helpBlock}
        </FormGroup>
      );
    }

    if (type === Array) {
      const selectedValue = this.state[key];
      const selectedOption = getOpt(flag.options, selectedValue);
      return (
        <FormGroup key={key} controlId={key}>
          <FormLabel>{label}</FormLabel>
          <Select
            name={key}
            isClearable={false}
            isDisabled={isDisabled}
            isSearchable={false}
            onChange={(value) => {
              this._updateArrayFlag(value, key);
            }}
            options={flag.options}
            value={selectedOption}
          />
          {helpBlock}
        </FormGroup>
      );
    }

    return (
      <FormGroup key={key} controlId={key}>
        <FormLabel>{label}</FormLabel>
        <FormControl
          type="text"
          value={this.state[key]}
          disabled={isDisabled}
          onChange={(e) => this._updateTextFlag(e, key)}
        />
        {helpBlock}
      </FormGroup>
    );
  }

  _renderBreadcrumbs(name) {
    if (!name) {
      return null;
    }
    let path;
    if (this._isDevice()) {
      path = `/devices/${this.props.model.device_id}`;
    } else {
      path = `/networks/${this.props.model.id}`;
    }

    return (
      <Breadcrumb>
        <Breadcrumb.Item href={this._isNetwork() ? "/networks" : "/devices"}>
          {this._isNetwork() ? "Networks" : "Devices"}
        </Breadcrumb.Item>
        <Breadcrumb.Item href={path}>{name}</Breadcrumb.Item>
        <Breadcrumb.Item active>Update flags</Breadcrumb.Item>
      </Breadcrumb>
    );
  }

  _renderFlags(flags) {
    const isDisabled =
      this._isDevice() && !this.state[Flags.OVERRIDE_NETWORK_FLAGS.key];
    return Object.values(flags).map((flag) => {
      return this._renderFlag(flag, isDisabled);
    });
  }

  _renderView() {
    const { model, readonly, storesLoaded } = this.props;
    const { errorMessage, submitted, successMessage } = this.state;

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    if (!model) {
      return <Redirect to="/404" />;
    }

    const [watchdogFlags, windowFlags, otherFlags, deviceOnlyFlag, webcamFlags] =
      this._groupFlags();

    return (
      <Row>
        <Col xs={12}>
          <Notification
            error={errorMessage}
            success={successMessage}
            successChildren={this._getSuccessMessage()}
          />
          {this._isDevice() && (
            <Row>
              <Col xs={12}>
                <Box title="Override Network Flags">
                  {this._renderFlag(deviceOnlyFlag, false)}
                </Box>
              </Col>
            </Row>
          )}
          {this._getNetworkFlagHelpText()}
          <Row>
            <Col xs={6}>
              <Box title="Watchdog Flags">
                {this._renderFlags(watchdogFlags)}
              </Box>
            </Col>
            <Col xs={6}>
              <Row>
                <Col xs={12}>
                  <Box title="Window Flags">
                    {this._renderFlags(windowFlags)}
                  </Box>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <Box title="Other Flags">{this._renderFlags(otherFlags)}</Box>
            </Col>
            {this._webcamSnapshotsEnabled() && (<Col xs={6}>
              <Box title="Webcam Snapshot Flags">{this._renderFlags(webcamFlags)}</Box>
            </Col>)
            }
          </Row>
          <Row>
            <Col xs={12}>
              <BackButton />
              <Button
                variant="primary"
                disabled={submitted}
                className={classNames("btn-update-flags", { hide: readonly })}
                onClick={this._submit}
              >
                Update
              </Button>
              <strong>Important:</strong> Device must be restarted for any
              changes to be implemented.
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }

  render() {
    const { model, type } = this.props;
    const modelName = model ? model.name : type;
    const view = this._renderView();

    return (
      <Page breadcrumbs={this._renderBreadcrumbs(modelName)} title={`Configure flags for ${modelName}`}>
        <Box noHeader={true}>{view}</Box>
      </Page>
    );
  }
}

export { DEVICE_TYPE, FlagsPage, NETWORK_TYPE };
